import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { AutocompleteMarker, CENTER_OF_EUROPE, CIRCLE_OPTIONS, convertLatLngToPoint, DefaultMapMarker, generateAutocompleteMarker, getCoordinateField, MAP_OPTIONS, MarkerLocation, markersComparator } from '../map-utils';
import { GoogleMap, MapCircle, MapInfoWindow, MapMarker, MapMarkerClusterer } from '@angular/google-maps';
import { Store } from '@ngrx/store';
import { MapLocalStore } from '../map-local-store.service';
import { delay, distinctUntilChanged, filter, startWith, take, takeUntil } from 'rxjs/operators';
import { memoize } from '../../../../core/decorators/memoize';
import { pairwise, Subject, switchMap } from 'rxjs';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { isCcCandidate } from '../../location-utils';
import { CCRowSetting } from '../../../../models/locations-models/cc-row-setting';
import { Location } from '../../../../models/locations-models/location';
import { Dictionary } from '@ngrx/entity';
import { markerSvgClicked, RowActions } from '../../location-list/state/location-list-actions';
import { CCRowActions } from '../../cc-list/state/cc-list-actions';
import { selectMarkersForSelectedLocations } from '../state/map-combined.selectors';
import { selectHoveredLocation } from '../../location-list/state/location-list-selectors';
import { round } from '@turf/helpers';
import { selectAreMarkersMinimized, selectIsCvButtonActive } from '../state/map-selectors';
import { LocationListApiService } from '../../location-list/location-list-api.service';
import { getClusterStyles } from '../../../../core/utilities';
import { selectHoveredCCLocation } from '../../cc-list/state/cc-list-selectors';
import { CCLocation } from '../../../../models/locations-models/cc-location';
import { AUTOCOMPLETE_MARKER_ID } from '../../constants';
import { MapUiComponent, PlaceAutocompleteMarkerEvent } from './map-ui/map-ui.component';
import { InfoWindowContentComponent } from './info-window-content/info-window-content.component';
import { LetDirective, PushPipe } from '@ngrx/component';
import { CcListApiService } from '../../cc-list/cc-list-api.service';
import { MapActions } from '../state/map-actions';
import { TooltipTextPipe } from './tooltip-text.pipe';
import { selectRightPanelRelativeWidth } from '../../state/locations-skeleton-selectors';

@Component({
  selector: 'app-default-map',
  templateUrl: './default-map.component.html',
  styleUrls: ['./default-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MapLocalStore],
  standalone: true,
  imports: [
    LetDirective,
    GoogleMap,
    MapMarkerClusterer,
    MapMarker,
    MapCircle,
    MapInfoWindow,
    InfoWindowContentComponent,
    MapUiComponent,
    PushPipe,
    TooltipTextPipe,
  ],
})
export class DefaultMapComponent implements OnInit, OnDestroy {

  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow; // access info window instance
  @ViewChild(GoogleMap) googleMap: GoogleMap; // access gmaps instance
  @ViewChild('cluster', { read: MapMarkerClusterer }) cluster: MapMarkerClusterer;
  @ViewChildren('mapMarkers', { read: MapMarker }) mapMarkers: QueryList<MapMarker>;

  protected readonly CIRCLE_OPTIONS = CIRCLE_OPTIONS();
  protected readonly MAP_OPTIONS = MAP_OPTIONS();
  protected readonly CLUSTER_ICON_STYLES = getClusterStyles();

  protected readonly delayedAndFilteredMarkers$ = this.localStore.delayedAndFilteredMarkers$;
  protected readonly mapViewModel$ = this.localStore.mapViewModel$;
  protected readonly markerTooltipViewModel$ = this.localStore.tooltipViewModel$;
  protected readonly mapLoading$ = this.localStore.mapLoading$;
  protected readonly infoWindowViewModel$ = this.localStore.infoWindowViewModel$;
  protected readonly cvButtonActive = this.store.selectSignal(selectIsCvButtonActive);
  protected readonly markersMinimized = this.store.selectSignal(selectAreMarkersMinimized);
  protected readonly rightPanelWidth = this.store.selectSignal(selectRightPanelRelativeWidth);

  private readonly componentDestroyed$: Subject<void> = new Subject<void>();

  constructor(private localStore: MapLocalStore,
              private store: Store,
              private actions$: Actions,
              private locationListApi: LocationListApiService,
              private ccListApi: CcListApiService) {
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
  }

  ngOnInit() {
    this.mapLoading$.pipe(
        filter(loading => !loading),
        take(1), // = map should be fully loaded, switch to markers observable until component is destroyed
        delay(250), // add a bit of delay to make sure map is rendered
        switchMap(() => this.store.select(selectMarkersForSelectedLocations)),
        // do not handleMapBoundsAndStreetViewPosition() all the time -> we do not need to call
        // that unless marker amount or their positions change
        distinctUntilChanged((a, b) => markersComparator(a, b)),
        takeUntil(this.componentDestroyed$),
    ).subscribe((markers) => {
      this.handleMapBounds(markers);
    });

    // center main map on marker on table svg click
    this.actions$.pipe(
        ofType(markerSvgClicked),
        takeUntil(this.componentDestroyed$),
    ).subscribe((action) => {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend({
        lat: action.location[getCoordinateField()].latitude,
        lng: action.location[getCoordinateField()].longitude,
      });

      this.googleMap.fitBounds(bounds);
    });

    // apply hover effects on location markers
    this.store.select(selectHoveredLocation)
        .pipe(
            startWith(null),
            pairwise(),
            concatLatestFrom(() => [this.store.select(selectAreMarkersMinimized)]),
            takeUntil(this.componentDestroyed$))
        .subscribe(([locations, markersMinimized]) =>
            this.highlightMarkerElements(locations, markersMinimized));

    this.store.select(selectHoveredCCLocation)
        .pipe(
            startWith(null),
            pairwise(),
            concatLatestFrom(() => [this.store.select(selectAreMarkersMinimized)]),
            takeUntil(this.componentDestroyed$))
        .subscribe(([ccLocations, markersMinimized]) =>
            this.highlightMarkerElements(ccLocations, markersMinimized));
  }

  // ******************************** HANDLERS ************************************************** //

  /**
   * Show and position marker tooltip,
   * highlight the respective table row and marker and increase z-index of respective marker
   * Toggle minimized/full-scale marker icon
   * @param event
   * @param focusedLocationId
   * @param marker
   * @param ccRowSettings
   */
  onMouseover(event,
              focusedLocationId: number,
              marker: DefaultMapMarker | AutocompleteMarker,
              ccRowSettings: Dictionary<CCRowSetting>) {
    if (!this.isDefaultMarker(marker)) {
      this.localStore.updateHoveredMarker(marker);
      return;
    }

    const point = convertLatLngToPoint(this.googleMap, event.latLng); // get xy position
    // update hovered marker in local store - tooltip derives data from it
    this.localStore.updateHoveredMarker(marker);
    // update tooltip position
    this.localStore.updateTooltipX(point.x);
    this.localStore.updateTooltipY(point.y);

    this.highlightGridRow(focusedLocationId, marker.markerLocations[0], ccRowSettings);
  }

  highlightGridRow(focusedLocationId: number,
                   markerLocation: MarkerLocation,
                   ccRowSettings: Dictionary<CCRowSetting>) {
    if (isCcCandidate(markerLocation.location)) {
      const ccRowSettingCopy = { ...ccRowSettings[markerLocation.location.placeId] };
      ccRowSettingCopy.hovered = true;
      this.store.dispatch(CCRowActions.updateCcRowSetting({
        focusedLocationId: focusedLocationId,
        ccRowSetting: ccRowSettingCopy,
      }));
    } else {
      this.store.dispatch(RowActions.updateHoveredRowId({ hoveredRowId: markerLocation.location.id }));
    }
  }

  /**
   * Hide marker tooltip
   * Remove highlight from respective table row and marker, decrease z-index of respective marker
   * Toggle minimized/full-scale marker icon
   * @param focusedLocationId
   * @param marker
   * @param ccRowSettings
   */
  onMouseout(focusedLocationId: number,
             marker: DefaultMapMarker | AutocompleteMarker,
             ccRowSettings: Dictionary<CCRowSetting>) {
    if (!this.isDefaultMarker(marker)) {
      this.localStore.updateHoveredMarker(null);
      return;
    }

    // hide tooltip
    this.localStore.updateHoveredMarker(null);
    this.removeHighlightFromGridRow(focusedLocationId, marker.markerLocations[0], ccRowSettings);
  }

  removeHighlightFromGridRow(focusedLocationId: number,
                             markerLocation: MarkerLocation,
                             ccRowSettings: Dictionary<CCRowSetting>) {
    if (isCcCandidate(markerLocation.location)) {
      const ccRowSettingCopy = { ...ccRowSettings[markerLocation.location.placeId] };
      ccRowSettingCopy.hovered = false;
      this.store.dispatch(CCRowActions.updateCcRowSetting({
        focusedLocationId: focusedLocationId,
        ccRowSetting: ccRowSettingCopy,
      }));
    } else {
      this.store.dispatch(RowActions.updateHoveredRowId({ hoveredRowId: null }));
    }
  }

  /**
   * Scroll grid row into view
   * @param markerLocation
   */
  scrollGridRowIntoView(markerLocation: MarkerLocation) {
    if (!markerLocation) {
      console.warn('\'scrollRowIntoView\': no MarkerLocation provided');
      return;
    }

    if (isCcCandidate(markerLocation.location)) {
      this.ccListApi.scrollRowToView(
          this.ccListApi.gridApi.getRowNode(markerLocation.location.placeId));
    } else this.locationListApi.scrollRowToView(
        this.locationListApi.gridApi.getRowNode(markerLocation.location.id.toString(10)));
  }

  /**
   * Map UI event handler
   */
  onToggleCvMode() {
    this.store.dispatch(MapActions.toggleCvButton());
  }

  /**
   * Map UI event handler
   */
  onToggleMinimizeMarkers() {
    this.store.dispatch(MapActions.toggleMarkersMinimizedButton());
  }

  /**
   * Map UI event handler
   */
  onPlaceAutocompleteMarker(e: PlaceAutocompleteMarkerEvent) {
    if (e) {
      this.localStore.updateAutocompleteMarker(generateAutocompleteMarker(e.minimized, e.position));
    } else this.localStore.updateAutocompleteMarker(null);
  }

  openInfoWindow(markerElement: MapMarker, marker: DefaultMapMarker | AutocompleteMarker) {
    if (!this.isDefaultMarker(marker)) return;

    this.localStore.updateInfoWindowMarker$(marker.id);
    this.infoWindow.open(markerElement, true);
  }

  closeInfoWindow() {
    this.infoWindow.close();
  }

  /**
   * BoundsChanged should set this to true, idle to false
   * Avoid doing heavy operations like rendering markers or clustering while map is loading
   * https://mapsplatform.google.com/resources/blog/google-maps-platform-best-practices-optimization-and-performance-tips/
   * @param loading
   */
  updateMapLoading(loading: boolean) {
    this.localStore.updateMapLoading(loading);
  }

  //*************************************** HELPERS **********************************************//

  isDefaultMarker(marker: DefaultMapMarker | AutocompleteMarker): marker is DefaultMapMarker {
    return marker.id !== AUTOCOMPLETE_MARKER_ID;
  }

  /**
   * When using immutable data src, markers will be re-rendered whenever there's an underlying
   * data update, which causes flickering - this solves it
   * https://angular.io/api/core/TrackByFunction
   * https://github.com/angular/angular/issues/11727
   * https://github.com/ngrx/store/issues/176
   * @param index
   * @param item
   */
  trackByFn(index: any, item: DefaultMapMarker | AutocompleteMarker) {
    return !this.isDefaultMarker(item) ?
        item.id :
        item.id +
        // so that cluster detects change if location amount changes (multi-marker)
        item.markerLocations?.length +
        // so that cluster detects change if options (icon specifically) changes
        JSON.stringify(item.options);
  }

  /**
   * Determine whether a circle should be rendered around a given marker
   * @param marker
   * @param isCcOn
   * @param focusedLocation
   */
  @memoize()
  showCircle(marker: DefaultMapMarker | AutocompleteMarker, isCcOn: boolean, focusedLocation: Location): boolean {
    // we're rendering a circle only in CC mode around default markers
    if (!isCcOn || !this.isDefaultMarker(marker)) {
      return false;
    }

    return (marker as DefaultMapMarker).markerLocations.some((l) =>
        l.location.id === focusedLocation?.id && isCcOn); // ... and only around the focused location
  }

  /**
   * Fit map bounds when the amount of markers changes or if their position changes
   * Center on EU when there are no markers at all.
   * Center street view position on CV markers in CV mode
   * @private
   * @param markers
   */
  private handleMapBounds(markers: Array<DefaultMapMarker | AutocompleteMarker>) {
    const bounds = new google.maps.LatLngBounds();

    // if there are no markers center on Europe
    if (!markers.length) {
      bounds.extend(CENTER_OF_EUROPE);
      this.googleMap?.fitBounds(bounds);
      this.googleMap?.googleMap?.setZoom(4);
      return;
    }

    // add marker position
    markers.forEach((m) => {
      bounds.extend(m.position);
    });

    this.googleMap?.fitBounds(bounds);
  }

  /**
   * Apply hover effects on Map markers
   * More efficient than changing the data src on hover which leads to costly re-rendering
   */
  private highlightMarkerElements(locations: Location[] | CCLocation[], markersMinimized: boolean) {
    const coordinateField = getCoordinateField();
    const getRoundedCoordinates = (location: Location | CCLocation) => {
      if (!location?.[coordinateField]) {
        return null;
      }

      return {
        latitude: round(location[coordinateField].latitude, 5),
        longitude: round(location[coordinateField].longitude, 5),
      };
    };
    const roundedCoordinatesCurrent = getRoundedCoordinates(locations[1]);
    const roundedCoordinatesPrevious = getRoundedCoordinates(locations[0]);

    const hoveredMarkerElement = roundedCoordinatesCurrent && this.mapMarkers?.find(me => {
      return me?.marker?.getPosition()?.lat() === roundedCoordinatesCurrent.latitude &&
          me?.marker?.getPosition()?.lng() === roundedCoordinatesCurrent.longitude;
    });

    const previouslyHoveredMarkerElement = roundedCoordinatesPrevious && this.mapMarkers?.find(me => {
      return me?.marker?.getPosition()?.lat() === roundedCoordinatesPrevious.latitude &&
          me?.marker?.getPosition()?.lng() === roundedCoordinatesPrevious.longitude;
    });

    // Skip clusters
    // Clusters don't have the 'size' property
    // TODO: think of a safer check should this become unreliable
    if (hoveredMarkerElement && hoveredMarkerElement.marker.getIcon()?.['size']) {
      hoveredMarkerElement.marker.setIcon({
        url: hoveredMarkerElement['_options']?.iconData?.default,
        labelOrigin: new google.maps.Point(40 / 2, 22),
      });

      hoveredMarkerElement.marker.setOptions({
        opacity: 0.7,
        label: hoveredMarkerElement['_options']?.iconData?.label ? hoveredMarkerElement['_options']?.iconData?.label : null,
      });
    }

    if (previouslyHoveredMarkerElement && previouslyHoveredMarkerElement.marker.getIcon()?.['size']) {
      previouslyHoveredMarkerElement.marker.setIcon({
        url: markersMinimized ? previouslyHoveredMarkerElement['_options']?.iconData?.minimized : previouslyHoveredMarkerElement['_options']?.iconData?.default,
        labelOrigin: new google.maps.Point(40 / 2, 22),
      });

      previouslyHoveredMarkerElement.marker.setOptions({
        opacity: 1,
        label: markersMinimized ? null : previouslyHoveredMarkerElement['_options']?.iconData?.label,
      });
    }
  }

}
